import { LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";

export type ButtonType = "primary" | "default" | "border" | "plain";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  type?: ButtonType;
  icon?: ReactNode;
  loading?: boolean;
}

const baseClasses = classNames(
  "inline-flex items-center justify-center h-12 text-sm transition-all duration-200 outline-none select-none gap-2 rounded-full font-medium cursor-pointer focus:outline-none focus:ring-0"
);

const typeClasses: Record<ButtonType, string> = {
  primary: classNames(
    "bg-primary text-white hover:bg-primary/85 disabled::opacity-50"
  ),
  default:
    "bg-background-card text-white hover:opacity-60 disabled::opacity-30",
  border: classNames(
    "bg-white text-graphite border border-gray-secondary-300 hover:bg-black/[.04] active:bg-black/[.04] active:opacity-50"
  ),
  plain: "",
};

const stateClasses = {
  disabled: "cursor-not-allowed opacity-60",
  loading: "cursor-default",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "default",
      className,
      icon,
      onClick,
      disabled = false,
      loading = false,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
      if (disabled || loading) return;
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        className={twMerge(
          classNames(baseClasses, typeClasses[type], {
            [stateClasses.disabled]: disabled,
            [stateClasses.loading]: loading,
          }),
          className
        )}
        onClick={handleClick}
        disabled={disabled}
        style={style}
        {...rest}
      >
        {loading && <LoadingOutlined className="animate-spin" />}
        {!loading && icon && (
          <span className="inline-flex items-center">{icon}</span>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
